// Default column widths sized to the header title, so a fresh plan shows every
// column name in full (header-only — cell content is ignored on purpose).
// Keep the font + spacing constants in sync with `.ag-header-cell` in
// src/styles/ag-grid-theme.css and DagColumnHeaderLabel.

const HEADER_FONT =
  "600 14px Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
const HEADER_HORIZONTAL_PADDING = 16 * 2
const GRIP_ICON_WIDTH = 14 + 4 // h-3.5 w-3.5 + gap-1
const REQUIRED_MARKER_WIDTH = 8 + 4 // "*" + gap-1
const SAFETY_MARGIN = 8
// Rough average glyph width when no canvas is available (tests/SSR)
const FALLBACK_CHAR_WIDTH = 8.5

let measureContext: CanvasRenderingContext2D | null | undefined
const textWidthCache = new Map<string, number>()

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    measureContext =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null
    if (measureContext) measureContext.font = HEADER_FONT
  }
  return measureContext
}

export function measureHeaderTextWidth(label: string): number {
  const cached = textWidthCache.get(label)
  if (cached !== undefined) return cached
  const ctx = getMeasureContext()
  const width = ctx
    ? ctx.measureText(label).width
    : label.length * FALLBACK_CHAR_WIDTH
  textWidthCache.set(label, width)
  return width
}

export interface HeaderWidthOptions {
  isReorderable?: boolean
  isRequiredInput?: boolean
  // Extra trailing header affordances (e.g. the bulk edit pencil)
  extraIconPx?: number
}

export function headerFitWidth(
  label: string,
  { isReorderable, isRequiredInput, extraIconPx = 0 }: HeaderWidthOptions = {}
): number {
  return Math.ceil(
    measureHeaderTextWidth(label) +
      HEADER_HORIZONTAL_PADDING +
      (isReorderable ? GRIP_ICON_WIDTH : 0) +
      (isRequiredInput ? REQUIRED_MARKER_WIDTH : 0) +
      extraIconPx +
      SAFETY_MARGIN
  )
}

/** Never narrower than the existing default; widened to fit the title. */
export function defaultColumnWidth(
  baseWidth: number,
  label: string,
  opts?: HeaderWidthOptions
): number {
  return Math.max(baseWidth, headerFitWidth(label, opts))
}
